import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Layout from '@/components/Layout';
import { toast } from 'sonner';
import { Save, RotateCcw, ArrowLeft, WifiOff } from 'lucide-react';
import offlineDB from '@/utils/offlineDB';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrderSignaturePad({ token, user, onLogout }) {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const sigCanvas = useRef(null);
  const [order, setOrder] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [signerName, setSignerName] = useState('');
  const [signatureDate, setSignatureDate] = useState(new Date().toISOString().split('T')[0]);

  // Check if user came from tours page
  const fromTours = location.state?.from === 'tours';
  
  const navigateBack = () => {
    if (fromTours) {
      navigate('/tours');
    } else {
      navigate('/orders');
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data);

      if (response.data.supplier_id) {
        const supplierResponse = await axios.get(`${API}/suppliers/${response.data.supplier_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSupplier(supplierResponse.data);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Bestellung');
      navigateBack();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!signerName.trim()) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    if (sigCanvas.current.isEmpty()) {
      toast.error('Bitte unterschreiben Sie');
      return;
    }

    try {
      const signatureData = sigCanvas.current.toDataURL();
      const signaturePayload = {
        order_id: orderId,
        signature_data: signatureData,
        signer_name: signerName,
        signature_date: signatureDate
      };

      // Check if online
      if (navigator.onLine) {
        // Online mode - send directly
        await axios.post(
          `${API}/orders/sign`,
          signaturePayload,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success('Bestellung erfolgreich unterschrieben');
        navigateBack();
      } else {
        // Offline mode - save to IndexedDB for later sync
        const pendingSignature = {
          id: `signature_${Date.now()}_${orderId}`,
          type: 'order',
          body: JSON.stringify(signaturePayload),
          timestamp: new Date().toISOString()
        };

        await offlineDB.addPendingSignature(pendingSignature);

        toast.success('Unterschrift offline gespeichert. Wird automatisch synchronisiert wenn Internet verfügbar ist.', {
          duration: 5000,
          icon: <WifiOff className="h-5 w-5" />
        });
        navigateBack();
      }
    } catch (error) {
      console.error('Error saving signature:', error);
      toast.error('Fehler beim Speichern der Unterschrift');
    }
  };

  const handleClear = () => {
    sigCanvas.current.clear();
  };

  if (isLoading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return null;
  }

  const isReadOnly = order.locked || order.signed;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="container mx-auto p-6 max-w-7xl">
        <Button
          onClick={() => navigateBack()}
          variant="outline"
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zurück
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* PDF Viewer */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">Bestellung: {order.filename}</h2>
            <div className="border rounded-lg overflow-hidden bg-white shadow-lg relative">
              {order.locked && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                  <div className="transform -rotate-45">
                    <div className="text-red-600 font-bold opacity-30" style={{
                      fontSize: '80px',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                      letterSpacing: '10px'
                    }}>
                      GESPERRT
                    </div>
                  </div>
                </div>
              )}
              <iframe
                src={`${BACKEND_URL}/api/orders/${orderId}/pdf-public`}
                className="w-full h-[600px]"
                title="Bestellung PDF"
              />
            </div>
            {supplier && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Lieferant:</h3>
                <p>{supplier.company_name}</p>
                {supplier.contact_person && <p className="text-sm text-gray-600">{supplier.contact_person}</p>}
              </div>
            )}
          </div>

          {/* Signature Pad */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">
              {order.signed ? 'Unterschrift' : 'Unterschreiben'}
            </h2>

            {order.signed ? (
              <div className="border rounded-lg p-6 bg-green-50">
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded border">
                    <img src={order.signature_data} alt="Unterschrift" className="max-w-full" />
                  </div>
                  <div>
                    <p className="font-semibold">Unterschrieben von:</p>
                    <p>{order.signer_name}</p>
                  </div>
                  <div>
                    <p className="font-semibold">Datum:</p>
                    <p>{order.signature_date}</p>
                  </div>
                </div>
              </div>
            ) : order.locked ? (
              <div className="border rounded-lg p-6 bg-red-50">
                <p className="text-red-800">
                  Diese Bestellung ist gesperrt und kann nicht unterschrieben werden. Bitte ordnen Sie erst einen Lieferanten zu.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="signer-name">Name des Unterzeichners *</Label>
                  <Input
                    id="signer-name"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="Ihr Name"
                  />
                </div>

                <div>
                  <Label htmlFor="signature-date">Datum *</Label>
                  <Input
                    id="signature-date"
                    type="date"
                    value={signatureDate}
                    onChange={(e) => setSignatureDate(e.target.value)}
                  />
                </div>

                <div>
                  <Label>Unterschrift *</Label>
                  <div className="border-2 border-gray-300 rounded-lg bg-white">
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{
                        className: 'w-full h-64 touch-none'
                      }}
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Bitte unterschreiben Sie im obigen Feld
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleSave}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    Unterschrift speichern
                  </Button>
                  <Button
                    onClick={handleClear}
                    variant="outline"
                    className="flex-1"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Zurücksetzen
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
